import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Play, Square, Navigation, Send } from "lucide-react";

// Pilot corridor Eagle Pass -> Mérida (for the "drive the route" demo).
const ROUTE = [
  [28.7091, -100.4995], [28.6790, -100.5050], [28.7000, -100.5236], [25.4383, -101.0053],
  [22.1565, -100.9855], [20.5888, -100.3899], [19.0414, -98.2063], [17.9892, -92.9475],
  [19.8301, -90.5349], [20.9674, -89.5926],
];

function lerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }

export default function TrackSim() {
  const [sp] = useSearchParams();
  const [endpoint, setEndpoint] = useState(sp.get("e") || "");
  const [id, setId] = useState(sp.get("id") || "van-1");
  const [lat, setLat] = useState("28.7091");
  const [lon, setLon] = useState("-100.4995");
  const [log, setLog] = useState([]);
  const [driving, setDriving] = useState(false);
  const timer = useRef(null);
  const step = useRef(0);

  const addLog = (m) => setLog((l) => [`${new Date().toLocaleTimeString()} — ${m}`, ...l].slice(0, 20));

  const send = async (la, lo) => {
    if (!endpoint) return toast.error("Paste the tracking Server URL first");
    const params = new URLSearchParams({ id, lat: la, lon: lo, timestamp: Math.floor(Date.now() / 1000), speed: "5", batt: "82", sim: "1" });
    try {
      const r = await fetch(`${endpoint}?${params}`);
      addLog(`${r.status} @ ${(+la).toFixed(4)}, ${(+lo).toFixed(4)}`);
      if (!r.ok) toast.error(`Server said ${r.status}`);
    } catch (e) { addLog(`network error: ${e}`); }
  };

  const drive = () => {
    setDriving(true); step.current = 0;
    timer.current = setInterval(() => {
      const seg = Math.floor(step.current);
      if (seg >= ROUTE.length - 1) { stop(); toast.success("Arrived in Mérida"); return; }
      const [la, lo] = lerp(ROUTE[seg], ROUTE[seg + 1], step.current - seg);
      setLat(la.toFixed(5)); setLon(lo.toFixed(5)); send(la, lo);
      step.current += 0.34;
    }, 2500);
  };
  const stop = () => { clearInterval(timer.current); setDriving(false); };
  useEffect(() => () => clearInterval(timer.current), []);

  const useMyLocation = () => navigator.geolocation?.getCurrentPosition((p) => {
    setLat(p.coords.latitude.toFixed(5)); setLon(p.coords.longitude.toFixed(5));
    send(p.coords.latitude, p.coords.longitude);
  }, () => toast.error("Location permission denied"));

  return (
    <div className="min-h-screen bg-sand-50 py-8 px-4" data-testid="track-sim-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl p-6 shadow-sm space-y-4">
        <h1 className="font-serif text-2xl text-charcoal-800">GPS simulator</h1>
        <p className="text-sm text-charcoal-500">Send test positions to a trip without a phone. Paste the trip's <b>Server URL</b> (from the admin trip screen).</p>
        <label className="text-sm block">Server URL
          <input data-testid="sim-endpoint" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm break-all" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://…/api/track/ingest/TOKEN" />
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-sm">Device id<input data-testid="sim-id" className="mt-1 w-full border rounded-lg px-2 py-2 text-sm" value={id} onChange={(e) => setId(e.target.value)} /></label>
          <label className="text-sm">Lat<input data-testid="sim-lat" className="mt-1 w-full border rounded-lg px-2 py-2 text-sm" value={lat} onChange={(e) => setLat(e.target.value)} /></label>
          <label className="text-sm">Lon<input data-testid="sim-lon" className="mt-1 w-full border rounded-lg px-2 py-2 text-sm" value={lon} onChange={(e) => setLon(e.target.value)} /></label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="sim-send-btn" onClick={() => send(lat, lon)} className="bg-terracotta text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Send size={14} /> Send one point</button>
          <button onClick={useMyLocation} className="border rounded-full px-4 py-2 text-sm flex items-center gap-1"><Navigation size={14} /> Use my location</button>
          {!driving
            ? <button data-testid="sim-drive-btn" onClick={drive} className="bg-green-600 text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Play size={14} /> Drive the pilot route</button>
            : <button onClick={stop} className="bg-charcoal-700 text-white rounded-full px-4 py-2 text-sm flex items-center gap-1"><Square size={14} /> Stop</button>}
        </div>
        <pre className="bg-sand-50 rounded-lg p-3 text-xs h-48 overflow-auto" data-testid="sim-log">{log.join("\n") || "No points sent yet."}</pre>
      </div>
    </div>
  );
}
